import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * Lead creation, answered by POST /opportunities.
 *
 * Eleven screens call this. They do not agree with each other: five post
 * multipart form data and six post JSON; the customer arrives as either
 * `customers_id` or `contact_id`; the loan mode is either `mode` or
 * `mode_opp`; and the purpose comes back at `data.response.extra_detail
 * .purpose_id` for some callers and `data.purposeID` for others. Xano
 * tolerated all of it, so this does too — every spelling is read on the way
 * in and every path is filled on the way out. Nothing is normalised in the
 * screens, because the point of this layer is that the screens do not change.
 *
 * Two reading quirks are deliberately preserved rather than fixed:
 * `response.id.contacts_id` and `interactions_id[0][0].note` resolve to
 * undefined here exactly as they did against Xano. Filling them would change
 * what those screens navigate with, which is a behaviour change dressed up as
 * a bug fix.
 */

type Payload<T> = AxiosRequestConfig<T>;

export type PostExistingOpportunityReqType = Partial<{
  headers: { Authorization: string };
}>;

export type PostExistingOpportunityResponseType = Partial<{}>;

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * The Xano interaction-channel ids.
 *
 * Mind the direction. The panel's two pickers are labelled "I contacted" and
 * "Customer contacted", but its own variables call the first one `inbound` —
 * the opposite of what the words mean. Ids 1-4 sit behind "I contacted", so
 * they are outbound contacts; 5-12 are how a customer reached us. The backend
 * stores the plain meaning, and `SCREEN_TYPE` below puts the screen's word
 * back when the panel reads the row again.
 */
const CHANNELS: Record<
  string,
  { channel: string; direction: "inbound" | "outbound"; source: string }
> = {
  "1": { channel: "call", direction: "outbound", source: "call" },
  "2": { channel: "email", direction: "outbound", source: "email" },
  "3": { channel: "visit", direction: "outbound", source: "meeting" },
  "4": { channel: "chat", direction: "outbound", source: "chat" },
  "5": { channel: "call", direction: "inbound", source: "call" },
  "6": { channel: "email", direction: "inbound", source: "email" },
  "7": { channel: "visit", direction: "inbound", source: "walk-in" },
  "8": { channel: "chat", direction: "inbound", source: "chat" },
  "9": { channel: "website", direction: "inbound", source: "website" },
  "10": {
    channel: "social_media",
    direction: "inbound",
    source: "social media",
  },
  "11": { channel: "referral", direction: "inbound", source: "referral" },
  "12": { channel: "referral", direction: "inbound", source: "referral" },
};

/** The words the lead carries for how it arrived. */
const CHANNEL_BY_ID: Record<string, string> = Object.fromEntries(
  Object.entries(CHANNELS).map(([key, value]) => [key, value.source]),
);

/** The backend's direction, back into the word the panel's pickers use. */
const SCREEN_TYPE = (direction: string | null | undefined): string =>
  direction === "outbound" ? "inbound" : "outbound";

/**
 * The label the interaction table looks up its icon by. `visit` covers both a
 * walk-in and a booked meeting, and only one of the two is on offer per
 * direction, so the pair names it exactly.
 */
const CHANNEL_LABEL = (
  channel: string | null | undefined,
  direction: string | null | undefined,
): string => {
  switch (channel) {
    case "call":
      return "Call";
    case "email":
      return "Email";
    case "chat":
      return "Chat";
    case "whatsapp":
      return "WhatsApp";
    case "visit":
      return direction === "outbound" ? "Meeting" : "Walk-in";
    case "social_media":
      return "Social Media";
    // Xano's own spelling, which is the value the icon table is keyed by.
    case "referral":
      return "Refferal";
    case "website":
      return "Website";
    default:
      return "Other";
  }
};

/** staff | partner | contacts, as the screens spell it, into the API's words. */
const REFERRAL_TYPES: Record<string, "staff" | "partner" | "contact"> = {
  staff: "staff",
  employee: "staff",
  partner: "partner",
  contacts: "contact",
  contact: "contact",
};

/**
 * One flat bag of fields, whichever way the caller sent them.
 *
 * Repeated `items[]` keys collapse into an array, and anything that looks
 * like JSON is parsed, so the reader below does not have to know whether it
 * is looking at form data or an object.
 */
function flatten(body: unknown): Record<string, any> {
  const out: Record<string, any> = {};

  const put = (rawKey: string, value: unknown) => {
    const key = rawKey.replace(/\[\]$/, "");
    let parsed: unknown = value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          parsed = JSON.parse(trimmed);
        } catch {
          parsed = value;
        }
      }
    }
    if (rawKey.endsWith("[]")) {
      (out[key] ??= []).push(parsed);
    } else {
      out[key] = parsed;
    }
  };

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    body.forEach((value, key) => put(key, value));
  } else if (body && typeof body === "object") {
    Object.entries(body as Record<string, unknown>).forEach(([key, value]) =>
      put(key, value),
    );
  }
  return out;
}

const text = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const asString = String(value).trim();
  // Form data stringifies everything, so an absent value arrives as the word,
  // and a screen that ran an id through Number() before an id became a uuid
  // sends NaN. Both mean "nothing was chosen".
  if (
    !asString ||
    asString === "undefined" ||
    asString === "null" ||
    asString === "NaN"
  ) {
    return undefined;
  }
  return asString;
};

/**
 * An id, where Xano used 0 to mean "none".
 *
 * A referral posted as partner 0 must not be read as a referral by partner
 * number zero, which is a real row in the organizations table.
 */
const id = (value: unknown): string | undefined => {
  const asString = text(value);
  return asString === "0" ? undefined : asString;
};

/** Xano's exp_time was a count of days. */
function followUpFromExpiry(expiry: unknown): string | undefined {
  const days = Number(text(expiry));
  if (!Number.isFinite(days) || days <= 0) return undefined;
  const when = new Date();
  when.setDate(when.getDate() + days);
  return when.toISOString();
}

type LeadDetail = {
  id: string;
  leadNo: number;
  customer: { id: string; name: string };
  product: string | null;
  productId: string | null;
  purposeId: string | null;
  sourceChannel: string | null;
  interactions: { id: string; note: string | null }[];
};

/** The Xano-shaped envelope every caller unpacks. */
function xanoShape(lead: LeadDetail, channelOfInteractionId?: string) {
  return {
    status: 200,
    data: {
      // Top-level for the callers that read it here, nested for the rest.
      purposeID: lead.purposeId,
      response: {
        id: String(lead.leadNo),
        contacts_id: lead.customer.id,
        interactions_id: lead.interactions.map((i) => i.id),
        extra_detail: { purpose_id: lead.purposeId },
        _contacts: [{ contactName: lead.customer.name }],
        product_id: lead.productId,
        _product: { name: lead.product },
        channel_of_interaction_id:
          channelOfInteractionId ?? lead.sourceChannel ?? null,
      },
    },
  };
}

async function createRetailLead(fields: Record<string, any>) {
  const customerId = text(fields.customers_id) ?? text(fields.contact_id);
  if (!customerId) throw new Error("A lead needs a customer.");

  const channelId = id(fields.channel_of_interaction_id);
  const sourceChannel =
    text(fields.source) ??
    (channelId ? CHANNEL_BY_ID[channelId] : undefined) ??
    text(fields.channel);

  const interactionDirection = channelId
    ? CHANNELS[channelId]?.direction
    : undefined;

  const referralType = REFERRAL_TYPES[text(fields.typeOfRefferal) ?? ""];

  // items[] carries the product and who the lead is assigned to. Only ever
  // one product in practice, and the API takes one, so the first wins.
  const items: any[] = Array.isArray(fields.items)
    ? fields.items
    : fields.items
      ? [fields.items]
      : [];
  const item = items[0] ?? {};

  const purposeId =
    text(fields.extra_detail?.purpose_id) ?? text(fields.purpose_id);

  const { data } = await moneefloApi.post("/opportunities", {
    customerId,
    // Xano's ids, not ours. The retail lead screen has them written into
    // it -- a product of 27 here is Dummy Hotel, and the lead simply
    // failed to save -- so they are resolved by the API rather than used
    // as though the two numberings agreed.
    xanoProductId: text(item.product_id),
    xanoPurposeId: purposeId,
    expectedAmount: text(fields.amount) ? Number(fields.amount) : undefined,
    country: text(fields.country),
    notes: text(item.notes) ?? text(fields.note),
    interactionNote: text(fields.note) ?? text(item.notes),
    nextFollowUpAt: followUpFromExpiry(fields.exp_time),
    assignedUserId: text(item.assignedTo),
    sourceChannel,
    interactionDirection,
    referralType,
    referredUserId:
      referralType === "staff" ? id(fields.refferedBy_staff) : undefined,
    referredOrgId:
      referralType === "partner" ? id(fields.refferedBy_partner) : undefined,
    referredCustomerId:
      referralType === "contact" ? id(fields.refferedBy_contacts) : undefined,
    branchOrgId: id(fields.outlet_branch) ?? id(fields.forex_branch),
    campaign: text(fields.campaign),
  });

  return xanoShape(data as LeadDetail, channelId);
}

/**
 * A corporate or partner lead: the company is registered first, then the
 * person named on it, then the lead itself. Xano did this in one call; here
 * it is three, and the organization is created first so a failure halfway
 * leaves a company to retry against rather than an orphaned contact.
 */
async function createOrganizationLead(
  fields: Record<string, any>,
  type: "corporate" | "partner",
) {
  const contactName = text(fields.contactName);
  const companyName = text(fields.company_name) ?? contactName;
  if (!companyName) throw new Error("A lead needs a company name.");

  const { data: organization } = await moneefloApi.post("/organizations", {
    name: companyName,
    type,
    email: text(fields.company_email) ?? text(fields.contactEmail),
    phone: text(fields.contactNumber),
    city: text(fields.contactAddress),
    // Xano let a partner be registered before its tier was decided; the API
    // insists on one, and a new partner starts at the bottom of the chain.
    ...(type === "partner" ? { partnerLevel: 3 } : {}),
  });

  const { data: customer } = await moneefloApi.post("/customers", {
    fullName: contactName ?? companyName,
    email: text(fields.contactEmail),
    phone: text(fields.contactNumber),
    city: text(fields.contactAddress),
    type: "business",
    source: `${type}_lead`,
  });

  const items: any[] = Array.isArray(fields.items)
    ? fields.items
    : fields.items
      ? [fields.items]
      : [];
  const item = items[0] ?? {};

  const channelId = id(fields.channel_of_interaction_id);
  const { data: lead } = await moneefloApi.post("/opportunities", {
    customerId: (customer as { id: string }).id,
    productId: text(item.product_id),
    notes: text(item.notes) ?? text(fields.note),
    interactionNote: text(fields.note) ?? text(item.notes),
    sourceChannel:
      text(fields.source) ??
      (channelId ? CHANNEL_BY_ID[channelId] : undefined) ??
      text(fields.channel),
    interactionDirection: channelId ? CHANNELS[channelId]?.direction : undefined,
    referredOrgId: (organization as { id: string }).id,
    referralType: type === "partner" ? "partner" : undefined,
  });

  return xanoShape(lead as LeadDetail, channelId);
}

export async function postExistingOpportunity(
  payload: Payload<PostExistingOpportunityReqType> & { data?: unknown },
): Promise<any> {
  const fields = flatten(payload.data);
  switch (text(fields.pipelineType)) {
    // Xano spelled it with two Os.
    case "coorporate":
    case "corporate":
      return createOrganizationLead(fields, "corporate");
    case "partner":
      return createOrganizationLead(fields, "partner");
    default:
      return createRetailLead(fields);
  }
}

/**
 * The forex lead, which the retail wizard posts instead of the call above
 * when the product group is forex.
 *
 * It carries a row per currency the customer asked for. The API holds one
 * amount on a lead, so the rows go into the note and the largest of them sets
 * the expected amount — a lead is a statement of intent, and the quote that
 * follows prices each currency properly.
 */
export async function createForexLeadApi(data: {
  // Ids were numbers in Xano and are uuids for people now, so both are taken
  // as they come rather than the screen coercing a uuid to NaN.
  customers_id: string | number;
  data: { [key: string]: any };
  interaction_id: string | number;
  reffered_staff_id: string | number;
  reffered_partner_id: string | number;
  meeting_staff_id: (string | number)[];
  reffered_contacts_id: string | number;
  meeting_time: number;
  note: string;
}): Promise<any> {
  const customerId = text(data.customers_id);
  if (!customerId) throw new Error("A lead needs a customer.");

  const products: any[] = Array.isArray(data.data?.products)
    ? data.data.products.filter(Boolean)
    : [];

  const referredStaff = id(data.reffered_staff_id);
  const referredPartner = id(data.reffered_partner_id);
  const referredContact = id(data.reffered_contacts_id);
  const referralType = referredPartner
    ? "partner"
    : referredContact
      ? "contact"
      : referredStaff
        ? "staff"
        : undefined;

  const wanted = products
    .map((p) => `${p.product_name}: ${p.product_qty}`)
    .join(", ");
  const largest = products.reduce(
    (most, p) => Math.max(most, Number(p.quantity) || 0),
    0,
  );

  const channelId = id(data.interaction_id);
  const { data: lead } = await moneefloApi.post("/opportunities", {
    customerId,
    // The forex step carries product ids hardcoded from Xano, which do not
    // line up with the new catalog's own ids.
    xanoProductId: products[0]?.product_id,
    currency: products[0]?.currency_want ?? products[0]?.currency_have,
    expectedAmount: largest > 0 ? largest : undefined,
    notes: [text(data.note), wanted].filter(Boolean).join(" — ") || undefined,
    interactionNote: text(data.note),
    sourceChannel: channelId ? CHANNEL_BY_ID[channelId] : undefined,
    interactionDirection: channelId ? CHANNELS[channelId]?.direction : undefined,
    referralType,
    referredUserId: referralType === "staff" ? referredStaff : undefined,
    referredOrgId: referralType === "partner" ? referredPartner : undefined,
    referredCustomerId: referralType === "contact" ? referredContact : undefined,
  });

  return xanoShape(lead as LeadDetail, channelId);
}

/* ---------------------------------------------------- one contact's leads */

export type GetOpportunityByCompanyReqType = Partial<{
  headers: { Authorization: string };
  companyID: string | number;
  contactID: string | number;
}>;

/**
 * These screens date rows with getRelativeDate, which takes epoch
 * milliseconds the way Xano returned them. An ISO string renders as
 * 01-Jan-1970.
 */
const epoch = (iso: string | null | undefined): number | null =>
  iso ? new Date(iso).getTime() : null;

type LeadRow = {
  id: string;
  leadNo: number;
  createdBy: string | null;
  customer: { id: string; name: string; phone: string | null };
  product: string | null;
  productId: string | null;
  category: string | null;
  stage: string;
  expectedAmountMinor: string | null;
  currency: string | null;
  country: string | null;
  assignedTo: string | null;
  sourceChannel: string | null;
  referredBy: string | null;
  branch: string | null;
  branchCity: string | null;
  purposeId: string | null;
  createdAt: string;
};

/**
 * The leads on a contact, for the table on their detail screen.
 *
 * Xano returned each lead with its relations pre-joined under underscored
 * keys, and the screen reads them directly, so the rows are rebuilt in that
 * shape. Fields Xano carried that the new model does not — the close reason,
 * the quotation PDF — are left absent rather than invented; the columns that
 * read them simply stay blank.
 */
export async function getOpportunityByCompany(
  payload: Payload<GetOpportunityByCompanyReqType> & {
    params?: { companyID?: string | number; contactID?: string | number };
  },
): Promise<{ data: any[] }> {
  const customerId = text(
    payload.params?.contactID ??
      payload.params?.companyID ??
      payload.data?.contactID ??
      payload.data?.companyID,
  );
  if (!customerId) return { data: [] };

  const { data } = await moneefloApi.get("/opportunities", {
    params: { customerId, perPage: 100 },
  });
  const leads = ((data?.items ?? []) as LeadRow[]) ?? [];

  return {
    data: leads.map((lead) => ({
      // The lead number, not the uuid: this is what the ID column prints and
      // what the screens hand back when they ask for the lead again. The API
      // resolves either.
      id: String(lead.leadNo),
      unique_id: String(lead.leadNo),
      _contacts: [
        {
          id: lead.customer.id,
          contactName: lead.customer.name,
          contactNumber: lead.customer.phone ?? "",
        },
      ],
      _product: lead.product
        ? { name: lead.product, subCategory: lead.category ?? "" }
        : null,
      product_details: [],
      // assignedTo of 0 is Xano's "nobody", which made the screen fall back to
      // the logged-in user's name. A name here means someone really is on it.
      assignedTo: lead.assignedTo ? 1 : 0,
      _staff: { fname: lead.assignedTo ?? "", city: lead.branchCity ?? "" },
      // Same rule for the creator, which the date's tooltip reads. Leads
      // brought over from Xano did not record one, and 0 makes the screen
      // fall back the way it always did rather than name the wrong person.
      createdBy: lead.createdBy ? 1 : 0,
      _createdBy: lead.createdBy ? { fname: lead.createdBy } : null,
      created_at: epoch(lead.createdAt),
      _channel_of_interaction: lead.sourceChannel
        ? [{ id: lead.sourceChannel, Channel: lead.sourceChannel }]
        : [],
      _refferedBy_partner: lead.referredBy ? { name: lead.referredBy } : null,
      _refferedBy_employee: lead.referredBy ? { name: lead.referredBy } : null,
      purpose_id: lead.purposeId,
      status: lead.stage,
      stage_tags: lead.stage,
      amount: lead.expectedAmountMinor
        ? Number(lead.expectedAmountMinor) / 100
        : null,
      currency: lead.currency,
      country: lead.country,
      interactions_id: [],
    })),
  };
}

/* ------------------------------------------------------------- one lead */

export type GetOpportunityByIdReqType = Partial<{
  headers: { Authorization: string };
  id: string | number;
}>;

type LeadDetailRow = {
  id: string;
  leadNo: number;
  createdBy: string | null;
  customer: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
  };
  product: string | null;
  productId: string | null;
  category: string | null;
  purpose: string | null;
  purposeId: string | null;
  stage: string;
  expectedAmountMinor: string | null;
  currency: string | null;
  country: string | null;
  notes: string | null;
  assignedTo: string | null;
  nextFollowUpAt: string | null;
  sourceChannel: string | null;
  campaign: string | null;
  referralType: string | null;
  referredBy: { id: string; name: string } | null;
  branch: { id: string; name: string } | null;
  createdAt: string;
  interactions: {
    id: string;
    channel: string;
    direction: string | null;
    note: string | null;
    occurredAt: string;
  }[];
};

/** call -> Call, social media -> Social Media. Xano stored them capitalised. */
const titleCase = (value: string | null | undefined): string =>
  (value ?? "")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");

/**
 * One lead, for the detail panel.
 *
 * Xano's lead_details_v3 returned every relation pre-joined under an
 * underscored key, and the panel reads those paths directly, so they are
 * rebuilt here rather than touching the six thousand lines that read them.
 *
 * What the new model does not carry is left absent, not invented: there is no
 * close reason, no document list, no loan flag and no per-currency forex
 * breakdown on a lead, so those blocks render their own empty state. The
 * interaction rows have no open/close of their own either — the panel sets
 * that field but never reads it.
 */
export async function getForexOpportunityById(
  payload: Payload<GetOpportunityByIdReqType>,
): Promise<{ status: number; data: any }> {
  const leadId = text(payload.data?.id);
  if (!leadId) return { status: 200, data: { leadDetail: {} } };

  const { data } = await moneefloApi.get(
    `/opportunities/${encodeURIComponent(leadId)}`,
  );
  const lead = data as LeadDetailRow;

  const contact = {
    id: lead.customer.id,
    linked_customer_id: lead.customer.id,
    contactName: lead.customer.name,
    contactEmail: lead.customer.email ?? "",
    contactNumber: lead.customer.phone ?? "",
    contactAddress: [],
    notes: lead.notes ?? "",
  };
  const channel = lead.sourceChannel
    ? [{ id: lead.sourceChannel, Channel: titleCase(lead.sourceChannel) }]
    : [];

  return {
    status: 200,
    data: {
      // The panel reads the partner off the envelope, not the lead.
      partner:
        lead.referralType === "partner" ? (lead.referredBy?.id ?? null) : null,
      leadDetail: {
        id: String(lead.leadNo),
        isOpen: lead.stage === "won" || lead.stage === "lost" ? "false" : "true",
        stage_tags: lead.stage,
        source: lead.campaign,
        amount: lead.expectedAmountMinor
          ? String(Number(lead.expectedAmountMinor) / 100)
          : "",
        country: lead.country,
        created_at: epoch(lead.createdAt),
        notes: lead.notes,
        followup_time: epoch(lead.nextFollowUpAt),
        extra_detail: { purpose_id: lead.purposeId },
        _product: lead.product
          ? {
              id: lead.productId,
              name: lead.product,
              subCategory: lead.category ?? "",
            }
          : null,
        product_details: [],
        documents: [],
        _staff: { fname: lead.assignedTo ?? "" },
        createdBy: lead.createdBy ? 1 : 0,
        _createdBy: lead.createdBy ? { fname: lead.createdBy } : null,
        _contacts: [contact],
        _customers: {
          full_name: lead.customer.name,
          email: lead.customer.email ?? "",
          phone_number: lead.customer.phone ?? "",
          address_id: [],
        },
        _channel_of_interaction: channel,
        _orderInteraction: channel,
        _outlet_branch: lead.branch ? { name: lead.branch.name } : null,
        refferedBy_staff: lead.referralType === "staff" ? lead.referredBy?.id : undefined,
        refferedBy_partner:
          lead.referralType === "partner" ? lead.referredBy?.id : undefined,
        _refferedBy_staff:
          lead.referralType === "staff"
            ? { fname: lead.referredBy?.name }
            : null,
        _refferedBy_partner:
          lead.referralType === "partner"
            ? { name: lead.referredBy?.name }
            : null,
        _refferedBy_contact:
          lead.referralType === "contact"
            ? [{ contactName: lead.referredBy?.name }]
            : [],
        // Xano nested each interaction one array deeper than it needed to be.
        interactions_id: (lead.interactions ?? []).map((interaction) => [
          {
            id: interaction.id,
            note: interaction.note ?? "",
            created_at: epoch(interaction.occurredAt),
            // The table picks the row's icon by Type then Channel, so both
            // have to be the words it keys them by, not the stored ones.
            _channel_of_interaction: [
              {
                Type: SCREEN_TYPE(interaction.direction),
                Channel: CHANNEL_LABEL(
                  interaction.channel,
                  interaction.direction,
                ),
                Source: "",
              },
            ],
            meetingStaff_id: [],
          },
        ]),
      },
    },
  };
}

/** The non-forex lead panel reads the same shape from the same endpoint. */
export const getOpportunityById = getForexOpportunityById;

/* -------------------------------------------------- logging an interaction */

export type PostCorporateInteractionsReqType = Partial<{
  headers: { Authorization: string };
  remarks: string;
  channel_of_interaction_id: string | number;
  contact: number;
  source: string;
  subject: string;
  meeting_time: Date | number;
  meetingStaff_id: (string | number)[];
  note: string;
  opportunity_id: string | number;
  quotation_pdf: string;
  ed_payment_mode: string;
  loan: boolean;
  geo_coordinates: {
    type: string;
    data: { lng: string; lat: string };
  };
  quotation_corporate?: string;
}>;

/**
 * Log a call, message or visit against a lead.
 *
 * Xano took a channel id and a pile of optional extras on the same call —
 * the meeting attendees, a quotation PDF, the staff member's coordinates.
 * Only the channel, the direction, the note and the time are kept here; the
 * rest belong to features that are not built yet, and quietly accepting them
 * would make them look stored.
 */
export async function postCorporateInteractions(
  payload: Payload<PostCorporateInteractionsReqType>,
): Promise<{ status: number; data: any }> {
  const leadId = text(payload.data?.opportunity_id);
  if (!leadId) throw new Error("An interaction needs a lead.");

  const channelId = id(payload.data?.channel_of_interaction_id);
  const channel = channelId ? CHANNELS[channelId] : undefined;
  const meetingTime = payload.data?.meeting_time;

  const { data } = await moneefloApi.post(
    `/opportunities/${encodeURIComponent(leadId)}/interactions`,
    {
      channel: channel?.channel ?? "other",
      direction: channel?.direction,
      note: text(payload.data?.note),
      // A meeting is logged for the time it is booked for, not for now.
      occurredAt: meetingTime
        ? new Date(meetingTime as number).toISOString()
        : undefined,
    },
  );

  return { status: 200, data };
}

/* --------------------------------------------------------- the lead lists */

export type GetLeadReqType = Partial<{
  headers: { Authorization: string };
  type: string;
  searchQuery: string;
  productType: string;
  product_id: string[];
  channel_of_interaction_id: string[];
  status: string[];
  stage_tag: string[];
}>;

/** The tabs are named for the product group; the catalog keys them shorter. */
const CATEGORY_BY_TAB: Record<string, string> = {
  remittance: "remittance",
  forex: "forex",
  visa: "visa",
  travel: "travel",
  studyabroad: "study_abroad",
  loan: "loan",
};

/**
 * Every lead on one product tab.
 *
 * Xano returned the whole list and the screen pages and filters it in the
 * browser, so this does the same rather than changing how the table behaves.
 * The category, the search and the stage are narrowed by the API; the rest of
 * the filter chips are applied here, because they are over fields the API
 * does not filter on and the list is already whole.
 */
export async function GetLeads(
  payload: Payload<GetLeadReqType> & { params?: GetLeadReqType },
): Promise<{ data: any[] }> {
  const query = payload.params ?? payload.data ?? {};
  const tab = (query.productType ?? "").toLowerCase().replace(/[\s_-]/g, "");
  const stages = (query.stage_tag ?? []).filter(Boolean);
  const channels = (query.channel_of_interaction_id ?? []).filter(Boolean);
  const products = (query.product_id ?? []).filter(Boolean);
  const statuses = (query.status ?? []).filter(Boolean);

  const leads = await allLeadPages({
    category: CATEGORY_BY_TAB[tab],
    search: text(query.searchQuery),
    // The API takes one stage; more than one is narrowed below instead.
    stage: stages.length === 1 ? stages[0] : undefined,
    productId: products.length === 1 ? products[0] : undefined,
  });

  const wanted = leads.filter((lead) => {
    if (stages.length > 1 && !stages.includes(lead.stage)) return false;
    if (products.length > 1 && !products.includes(lead.productId ?? ""))
      return false;
    if (channels.length > 0) {
      const channel = lead.sourceChannel ?? "";
      const ids = channels
        .map((channelId: string) => CHANNELS[channelId]?.source)
        .filter(Boolean);
      if (!ids.includes(channel)) return false;
    }
    // Xano's status was the close reason, which the new model does not carry,
    // so a status chip narrows to nothing rather than silently to everything.
    if (statuses.length > 0) return false;
    return true;
  });

  return { data: wanted.map(xanoLeadRow) };
}

async function allLeadPages(
  params: Record<string, string | undefined>,
): Promise<LeadRow[]> {
  const rows: LeadRow[] = [];
  for (let page = 1; page <= 20; page += 1) {
    const { data } = await moneefloApi.get("/opportunities", {
      params: { ...params, page, perPage: 100 },
    });
    const items = (data?.items ?? []) as LeadRow[];
    rows.push(...items);
    if (items.length < 100 || rows.length >= Number(data?.total ?? 0)) break;
  }
  return rows;
}

/** One row in the shape the lead tables read. */
function xanoLeadRow(lead: LeadRow) {
  const channel = lead.sourceChannel;
  const entry = Object.values(CHANNELS).find((c) => c.source === channel);
  return {
    id: String(lead.leadNo),
    pipelineType: "retail",
    customers_id: lead.customer.id,
    contacts_id: lead.customer.id,
    _customers: { full_name: lead.customer.name },
    customer_detail: { full_name: lead.customer.name },
    _contacts: [
      {
        contactName: lead.customer.name,
        contactNumber: lead.customer.phone ?? "N/A",
      },
    ],
    _product: lead.product
      ? { name: lead.product, subCategory: lead.category ?? "" }
      : null,
    product_details: [],
    _channel_of_interaction: channel
      ? [
          {
            id: channel,
            Channel: CHANNEL_LABEL(
              entry?.channel ?? "other",
              entry?.direction,
            ),
            Type: SCREEN_TYPE(entry?.direction),
            source: "",
          },
        ]
      : [],
    _createdBy: lead.createdBy ? { fname: lead.createdBy } : null,
    _assignedTo: {
      fname: lead.assignedTo ?? "",
      city: lead.branchCity ?? "",
    },
    created_at: epoch(lead.createdAt),
    stage_tags: lead.stage,
    purpose_id: lead.purposeId,
    interactions_id: [],
    walkin_branch: lead.branch ? { city: lead.branchCity ?? "" } : null,
    refferedBy_partner: lead.referredBy ? { name: lead.referredBy } : null,
  };
}

/* ------------------------------------- the lead detail panel's edits */

export type PutUpdateOpportunityStatusReqType = Partial<{
  headers: { Authorization: string };
  id: string | number;
  isopen: string;
  reasonToClose: string;
  assignedto_id: string | number;
  followup_time: Date | string;
  reminder_note: string;
  notes: string;
}>;

/**
 * Reassign a lead, set its follow-up reminder, or close it.
 *
 * The panel sends one of the three at a time and Xano had them on a single
 * PATCH, so this keeps that shape: whichever fields are present decide what
 * happens. `followup_time` arrives as epoch milliseconds in a string, which
 * is what the date picker produces.
 */
export async function putUpdateOpportunityStatus(
  payload: Payload<PutUpdateOpportunityStatusReqType>,
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  const id = text(body.id);
  if (!id) throw new Error("A lead is needed to update it.");

  const followUp = body.followup_time;
  const patch: Record<string, unknown> = {};

  if (body.assignedto_id) patch.assignedUserId = text(body.assignedto_id);

  // An explicit empty followup_time is how the panel clears a reminder, so
  // "absent" and "cleared" have to stay distinguishable.
  if ("followup_time" in body) {
    patch.nextFollowUpAt = followUp
      ? new Date(Number(followUp) || String(followUp)).toISOString()
      : null;
  }
  if (body.reminder_note) patch.followUpNote = body.reminder_note;
  if (body.reasonToClose) patch.closeReason = body.reasonToClose;

  const { data } = await moneefloApi.patch(`/opportunities/${id}`, patch);
  return { status: 200, data };
}

export type ClearReminderReqType = Partial<{
  headers: { Authorization: string };
  opportunity_id: string | number;
}>;

/** Drops the reminder without touching anything else on the lead. */
export async function clearReminderPatch(
  payload: Payload<ClearReminderReqType>,
): Promise<{ status: number; data: unknown }> {
  const id = text(payload.data?.opportunity_id);
  if (!id) throw new Error("A lead is needed to clear its reminder.");

  const { data } = await moneefloApi.patch(`/opportunities/${id}`, {
    nextFollowUpAt: null,
  });
  return { status: 200, data };
}

/* --------------------------------- the lead panel's Details and Files */

export type GetContactDetailByIdReqType = Partial<{
  headers: { Authorization: string };
  id: string | number;
}>;

/**
 * The Details tab for a retail lead.
 *
 * Xano's contact row. The panel reads a mixture of camelCase and
 * snake_case off it — `contactName` beside `full_name`, `contactEmail`
 * beside `email` — because two screens grew against two Xano endpoints, so
 * both spellings are filled rather than picking one and leaving half the
 * tab blank.
 */
export async function getContactDetailById(
  payload: Payload<GetContactDetailByIdReqType>,
): Promise<{ status: number; data: Record<string, unknown> }> {
  const id = text(payload.data?.id);
  if (!id) throw new Error("A contact is needed.");

  const { data } = await moneefloApi.get(`/customers/${id}`);
  const customer = data as {
    id: string;
    fullName: string;
    email: string | null;
    phone: string | null;
    city: string | null;
    state: string | null;
    notes: string | null;
    owner: string | null;
  };

  return {
    status: 200,
    data: {
      id: customer.id,
      contactName: customer.fullName,
      full_name: customer.fullName,
      contactEmail: customer.email ?? "",
      email: customer.email ?? "",
      contactNumber: customer.phone ?? "",
      phone_number: customer.phone ?? "",
      contactAddress: [customer.city ?? "", customer.state ?? ""],
      address_line_: [customer.city, customer.state].filter(Boolean).join(", "),
      notes: customer.notes ?? "",
      // A retail contact has no company behind it, and the tab hides the
      // block when this is absent rather than showing empty rows.
      companyDetail: null,
      partnerDetail: null,
      partner_linked: null,
      entity_table_id: null,
      customer: true,
      contactDetails: null,
    },
  };
}

export type GetCompanyByIdReqType = Partial<{
  headers: { Authorization: string };
  id: string | number;
}>;

/** The same tab for a corporate lead, where the record is an organization. */
export async function getCompanyById(
  payload: Payload<GetCompanyByIdReqType>,
): Promise<{ status: number; data: Record<string, unknown> }> {
  const id = text(payload.data?.id);
  if (!id) throw new Error("A company is needed.");

  const { data } = await moneefloApi.get(`/organizations/${id}`);
  const org = data as {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    city: string | null;
    state: string | null;
    type: string;
  };

  return {
    status: 200,
    data: {
      id: org.id,
      contactName: org.name,
      full_name: org.name,
      contactEmail: org.email ?? "",
      email: org.email ?? "",
      contactNumber: org.phone ?? "",
      phone_number: org.phone ?? "",
      contactAddress: [org.city ?? "", org.state ?? ""],
      address_line_: [org.city, org.state].filter(Boolean).join(", "),
      notes: "",
      companyDetail: { id: org.id, name: org.name },
      partnerDetail: org.type === "partner" ? { id: org.id, name: org.name } : null,
      partner_linked: org.type === "partner" ? org.id : null,
      entity_table_id: org.id,
      customer: false,
      contactDetails: null,
    },
  };
}

export type DownloadDocReqType = Partial<{
  headers: { Authorization: string };
  opportunity_id: string | number;
}>;

/**
 * The Files tab: documents already held for this contact.
 *
 * Xano keyed these off the contact, which is why the screen sends
 * `contacts_id` as a query param rather than in the body.
 */
export async function downloadDoc(
  payload: Payload<DownloadDocReqType> & {
    params?: { contacts_id?: string | number };
  },
): Promise<{ status: number; data: unknown[] }> {
  const id = text(payload.params?.contacts_id ?? payload.data?.opportunity_id);
  if (!id) return { status: 200, data: [] };

  const { data } = await moneefloApi.get(`/customers/${id}/documents`);
  return { status: 200, data: (data ?? []) as unknown[] };
}

export type editProductModeReqType = Partial<{
  headers: { Authorization: string };
  opportunity_id: number | string;
  mode_opp: string;
  took_loan: boolean;
}>;

/**
 * Changes a lead's education mode, and whether a loan is involved.
 *
 * Xano stored the mode as a string on the opportunity. Here the two
 * together identify a purpose — "Education CIBC with loan" is one purpose,
 * "without loan" another — so the pair is resolved against the catalog
 * rather than sent as free text. Resolving it here also means a mode the
 * catalog does not have fails loudly instead of writing a purpose nobody
 * can price.
 */
export async function editProductMode(
  payload: Payload<editProductModeReqType>,
): Promise<{ status: number; statusText: string; data: unknown }> {
  const body = payload.data ?? {};
  const id = text(body.opportunity_id);
  const mode = String(body.mode_opp ?? "").trim();
  if (!id) throw new Error("A lead is needed to change its mode.");
  if (!mode) throw new Error("Pick a mode first.");

  const { data: purposes } = await moneefloApi.get("/catalog/purposes");
  const rows = (purposes ?? []) as {
    id: string | number;
    mode: string | null;
    isLoan: boolean;
  }[];

  const wantsLoan = Boolean(body.took_loan);
  const match =
    rows.find(
      (p) =>
        String(p.mode ?? "").toLowerCase() === mode.toLowerCase() &&
        Boolean(p.isLoan) === wantsLoan,
    ) ??
    // Via Transfer has no loan variant, so fall back to the mode alone
    // rather than refusing a change the screen offers.
    rows.find((p) => String(p.mode ?? "").toLowerCase() === mode.toLowerCase());

  if (!match) throw new Error(`No purpose is set up for ${mode}.`);

  const { data } = await moneefloApi.patch(`/opportunities/${id}`, {
    purposeId: String(match.id),
  });
  return { status: 200, statusText: "OK", data };
}

export type RequestDocReqType = Partial<{
  headers: { Authorization: string };
  opportunity_id: string | number;
  exp_time: string;
  note: string;
  interaction_note: string;
  channel: string;
  doc_list: { filename: string }[];
  channel_of_interaction_id: string;
}>;

/**
 * Asks a lead for documents, and logs the contact that did the asking.
 *
 * One call because that is one action to the person doing it; the backend
 * writes the requests and the interaction together, so a request can never
 * exist with no record of who made it. `exp_time` arrives as epoch
 * milliseconds from the date picker.
 */
export async function requestDoc(
  payload: Payload<RequestDocReqType>,
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  const id = text(body.opportunity_id);
  if (!id) throw new Error("A lead is needed to request documents.");

  const documents = (body.doc_list ?? [])
    .map((doc) => ({ label: String(doc?.filename ?? "").trim() }))
    .filter((doc) => doc.label.length > 0);
  if (documents.length === 0) throw new Error("Pick at least one document.");

  const expires = body.exp_time
    ? new Date(Number(body.exp_time) || String(body.exp_time))
    : undefined;

  const { data } = await moneefloApi.post(
    `/opportunities/${id}/documents/request`,
    {
      documents,
      note: body.note || undefined,
      expiresAt:
        expires && !Number.isNaN(expires.getTime())
          ? expires.toISOString()
          : undefined,
      interactionNote: body.interaction_note || undefined,
      channel: CHANNEL_BY_ID[Number(body.channel_of_interaction_id)] ?? undefined,
    },
  );
  return { status: 200, data };
}

export type PutUpdateInteractionStatusReqType = Partial<{
  headers: { Authorization: string };
  id: string | number;
  isOpen: string;
  quotation_pdf: string;
  geo_coordinates: unknown;
}>;

/**
 * Closes off a lead's open interaction.
 *
 * Xano kept an `isOpen` flag on the interaction; here an interaction is a
 * record of something that happened and is never reopened, so what the
 * panel is really saying is "this lead has been dealt with for now" —
 * which is the reminder being cleared.
 */
export async function putUpdateInteractionStatus(
  payload: Payload<PutUpdateInteractionStatusReqType>,
): Promise<{ status: number; data: unknown }> {
  const id = text(payload.data?.id);
  if (!id) throw new Error("An interaction is needed.");

  const { data } = await moneefloApi.patch(`/opportunities/${id}`, {
    nextFollowUpAt: null,
  });
  return { status: 200, data };
}
